import React, { useState, useEffect } from 'react';
import { getAllPromos, createPromo, updatePromo, deletePromo } from '../../services/api';
import '../../styles/PromoManagement.css';

interface Promo {
  id: number;
  name: string;
  code: string;
  amount: number;
  percent: number;
  quota: number;
  createdAt: string;
  updatedAt: string;
}

const PromoManagement: React.FC = () => {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
  const [editingPromo, setEditingPromo] = useState<Promo | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    discountType: 'amount',  // 'amount' or 'percent'
    discountValue: '',
    quota: '0'
  });
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    fetchPromos();
  }, []);

  const fetchPromos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getAllPromos();
      setPromos(data);
    } catch (err: any) {
      console.error('Failed to fetch promo codes:', err);
      setError(err.response?.data?.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      discountType: 'amount',
      discountValue: '',
      quota: '0'
    });
    setEditingPromo(null);
    setIsAddingNew(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Promo name is required');
      return false;
    }
    
    if (!formData.code.trim()) {
      setError('Promo code is required');
      return false;
    }

    if (!formData.discountValue || parseFloat(formData.discountValue) <= 0) {
      setError(`${formData.discountType === 'amount' ? 'Amount' : 'Percentage'} must be greater than 0`);
      return false;
    }

    if (formData.discountType === 'percent' && parseFloat(formData.discountValue) > 100) {
      setError('Percentage cannot be greater than 100%');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    
    try {
      const promoData: any = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        quota: parseInt(formData.quota)
      };
      
      // Set either amount or percent based on discount type
      if (formData.discountType === 'amount') {
        promoData.amount = parseFloat(formData.discountValue);
        promoData.percent = 0;
      } else {
        promoData.percent = parseFloat(formData.discountValue);
        promoData.amount = 0;
      }
      
      let response;
      if (editingPromo) {
        response = await updatePromo(editingPromo.id.toString(), promoData);
        setSuccess(`Promo code ${promoData.code} updated successfully`);
      } else {
        response = await createPromo(promoData);
        setSuccess(`Promo code ${promoData.code} created successfully`);
      }
      
      resetForm();
      await fetchPromos();
    } catch (err: any) {
      console.error('Failed to save promo code:', err);
      
      if (err.response?.status === 409) {
        setError('A promo code with this code already exists');
      } else {
        setError(err.response?.data?.message || 'Failed to save promo code');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPromo = (promo: Promo) => {
    setEditingPromo(promo);
    
    // Determine discount type
    const discountType = promo.amount > 0 ? 'amount' : 'percent';
    const discountValue = discountType === 'amount' ? promo.amount.toString() : promo.percent.toString();
    
    setFormData({
      name: promo.name,
      code: promo.code,
      discountType,
      discountValue,
      quota: promo.quota.toString()
    });
    
    setIsAddingNew(true);
  };

  const handleDeletePromo = async (promoId: number) => {
    if (!window.confirm('Are you sure you want to delete this promo code? This action cannot be undone.')) {
      return;
    }
    
    try {
      setError(null);
      setSuccess(null);
      
      await deletePromo(promoId.toString());
      setSuccess('Promo code deleted successfully');
      await fetchPromos();
    } catch (err: any) {
      console.error('Failed to delete promo code:', err);
      setError(err.response?.data?.message || 'Failed to delete promo code');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Function to generate a random 6-character alphanumeric code (all uppercase)
  const generateRandomCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setFormData({
      ...formData,
      code: result
    });
  };

  if (loading && promos.length === 0) {
    return (
      <div className="promo-management">
        <h2>Promo Code Management</h2>
        <div className="loading-indicator">Loading promo codes...</div>
      </div>
    );
  }

  return (
    <div className="promo-management">
      <h2>Promo Code Management</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      {!isAddingNew ? (
        <div className="promo-actions">
          <button 
            className="add-promo-button"
            onClick={() => setIsAddingNew(true)}
          >
            Add New Promo Code
          </button>
        </div>
      ) : (
        <div className="promo-form-container">
          <h3>{editingPromo ? 'Edit Promo Code' : 'Add New Promo Code'}</h3>
          
          <form className="promo-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Promo Name:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g. Summer Sale"
                required
                disabled={submitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="code">Promo Code:</label>
              <div className="code-input-container">
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="e.g. SUMMER2023"
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="generate-code-button"
                  onClick={generateRandomCode}
                  disabled={submitting}
                >
                  Randomize
                </button>
              </div>
              <small>Promo code will be automatically converted to uppercase</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="discountType">Discount Type:</label>
              <select
                id="discountType"
                name="discountType"
                value={formData.discountType}
                onChange={handleInputChange}
                disabled={submitting}
              >
                <option value="amount">Fixed Amount</option>
                <option value="percent">Percentage</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="discountValue">
                {formData.discountType === 'amount' ? 'Discount Amount (IDR):' : 'Discount Percentage (%):'}
              </label>
              <input
                type="number"
                id="discountValue"
                name="discountValue"
                value={formData.discountValue}
                onChange={handleInputChange}
                placeholder={formData.discountType === 'amount' ? 'e.g. 50000' : 'e.g. 10'}
                min="0"
                max={formData.discountType === 'percent' ? '100' : undefined}
                required
                disabled={submitting}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="quota">Usage Quota:</label>
              <input
                type="number"
                id="quota"
                name="quota"
                value={formData.quota}
                onChange={handleInputChange}
                placeholder="Enter 0 for unlimited usage"
                min="0"
                disabled={submitting}
              />
              <small>Enter 0 for unlimited usage</small>
            </div>
            
            <div className="form-actions">
              <button
                type="submit"
                className="save-button"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : (editingPromo ? 'Update Promo' : 'Create Promo')}
              </button>
              
              <button
                type="button"
                className="cancel-button"
                onClick={() => {
                  resetForm();
                  setError(null);
                }}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      <div className="promo-list">
        <h3>Existing Promo Codes</h3>
        
        {promos.length === 0 ? (
          <div className="no-promos">No promo codes found.</div>
        ) : (
          <table className="promo-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Discount</th>
                <th>Quota</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promos.map(promo => (
                <tr key={promo.id}>
                  <td>{promo.name}</td>
                  <td className="promo-code">{promo.code}</td>
                  <td>
                    {promo.amount > 0 
                      ? `Rp ${promo.amount.toLocaleString('id-ID')}` 
                      : `${promo.percent}%`}
                  </td>
                  <td>{promo.quota === 0 ? 'Unlimited' : promo.quota}</td>
                  <td>{formatDate(promo.createdAt)}</td>
                  <td className="actions">
                    <button
                      className="edit-button"
                      onClick={() => handleEditPromo(promo)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-button"
                      onClick={() => handleDeletePromo(promo.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PromoManagement; 